import mixpanel from 'mixpanel-browser';
import {RefiFunnelContexts, RefiFunnelEvents} from '@deliveriesondemand/dod-data';

import {encodedOfferIdRE, getCookie, getOfferId, isStipsMode} from '-/util/util';
import {getCsrfToken} from '-/util/axios';

/**
 * Funnel telemetry, emitted on two rails.
 *
 * Mixpanel is the rail we read day to day; the server rail (a REFI_FUNNEL_STEP tracked
 * action per event) is the one that survives ad blocking and that AdminApp reports from.
 * Both carry the same event names, taken from dod-data, so the two can be reconciled.
 *
 * Four things this has to get right:
 *
 *  - No field values, ever. Auth progress is a character count and a boolean. The server
 *    scrubs anything else, but nothing here should rely on that.
 *  - The new-offer route (?offerId=new|cash|finance) has no offer to attach events to
 *    until createOffer returns, so events raised before then are buffered and flushed
 *    once a real offer id exists.
 *  - The hero CTA navigates immediately, so its event has to outlive the transition.
 *    That is `keepalive`, not sendBeacon: /api/refi is behind CSRF and sendBeacon cannot
 *    set the token header.
 *  - Nothing is ever posted without that token. Telemetry is best effort, but a tokenless
 *    post is not best effort -- it is a certain 403, and each one raises an
 *    INVALID_CSRF_TOKEN notification to every admin. See `drain` (sc-25961).
 */

const ENDPOINT = '/api/refi/funnelEvent';
const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 20;
const MAX_BUFFER = 60;
const MAX_TOKENLESS_FLUSHES = 3;

class FunnelService {
    constructor() {
        if (FunnelService.instance) {
            return FunnelService.instance;
        }

        FunnelService.instance = this;
        this.queue = [];
        this.flushTimer = null;
        this.tokenlessFlushes = 0;
        this.offerId = null;
        this.variant = null;
        this.variantSource = null;

        if (typeof window !== 'undefined' && window.addEventListener) {
            // pagehide fires on bfcache navigations where unload does not. It is the one
            // flush that cannot wait for anything, so it reads whatever token exists.
            window.addEventListener('pagehide', () => this.flush({sync: true}));
        }

        // Ask for the CSRF token now rather than at the first post. /api/refi is behind
        // csurf and the cookie is only issued by /api/ping, so without this the earliest
        // events race the bootstrap - and the earliest event is HERO_ADVANCED, the
        // primary metric for every landing test.
        this.primeCsrfToken();
    }

    /**
     * The offer id events are attributed to. Falls back to the one in the URL, which is
     * absent (or the literal "new"/"cash"/"finance") on the new-offer route.
     */
    getOfferId() {
        if (this.offerId) {
            return this.offerId;
        }

        const fromUrl = getOfferId();

        return encodedOfferIdRE.test(fromUrl || '') ? fromUrl : null;
    }

    /**
     * Called once createOffer has minted a real offer, so anything the member did before
     * that point can still be attributed.
     */
    setOfferId(offerId) {
        if (!encodedOfferIdRE.test(offerId || '')) {
            return;
        }

        this.offerId = offerId;

        // createOffer reloads the page on the next statement, so this cannot await
        // anything. The token is guaranteed by now: createOffer was itself a POST.
        this.flush({sync: true});
    }

    getContext() {
        return isStipsMode() ? RefiFunnelContexts.STIPS : RefiFunnelContexts.APP;
    }

    /**
     * The landing page this session was shown, and how it was chosen.
     *
     * Session-scoped rather than per-event, like `context`: the arm a member landed in
     * is a property of the visit, and every step they reach afterwards has to be
     * attributable to it or the landing pages cannot be compared at all. Set once by
     * `Hero` before the first event; see UIService#getLandingPage.
     *
     * Registered as Mixpanel super properties as well, so the arm reaches events raised
     * by MixpanelService rather than through here.
     */
    setVariant(variant, source) {
        if (!variant) {
            return;
        }

        this.variant = variant;
        this.variantSource = source || null;

        try {
            mixpanel.register({
                variant,
                ...source && {variantSource: source}
            });
        } catch (ex) {
            // Analytics must never break the landing page.
        }
    }

    /**
     * @param {String} event   a member of RefiFunnelEvents
     * @param {Object} opts    {step, from, detail, immediate}
     */
    track(event, opts = {}) {
        const {step, from, detail, immediate = false} = opts;
        const context = opts.context || this.getContext();
        const {variant, variantSource} = this;

        try {
            mixpanel.track(event, {
                context,
                ...step && {step},
                ...from && {from},
                // Also registered as super properties, which is what carries the arm onto
                // events MixpanelService raises. Repeated per event on purpose: super
                // properties are merged inside the library, so without this the event
                // panel -- which records what is PASSED to track -- cannot show the arm,
                // and neither can anything else reading the call site.
                ...variant && {variant},
                ...variant && variantSource && {variantSource},
                ...detail
            });
        } catch (ex) {
            // Never let telemetry break a step transition.
        }

        this.queue.push({
            at: new Date().toISOString(),
            context,
            ...step && {step},
            ...from && {from},
            ...detail && Object.keys(detail).length && {detail},
            // Siblings of `step`, not members of the `detail` whitelist -- the
            // ServiceLayer validates them that way too (app/refi/validation.js).
            ...variant && {variant},
            ...variant && variantSource && {variantSource},
            event
        });

        // Bound the queue if the member is offline or the offer never materialises.
        // Drop the NEWEST, not the oldest: the earliest events in a buffered session are
        // the hero view and advance, which are the primary metric. A late
        // employment-page navigation is worth less than the landing decision.
        if (this.queue.length > MAX_BUFFER) {
            this.queue.length = MAX_BUFFER;
        }

        if (immediate) {
            this.flush({keepalive: true});
        } else {
            this.scheduleFlush();
        }
    }

    scheduleFlush() {
        if (this.flushTimer) {
            return;
        }

        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this.flush();
        }, FLUSH_DELAY_MS);
    }

    /**
     * @param {Boolean} opts.keepalive  request must survive a navigation
     * @param {Boolean} opts.sync       cannot await anything (pagehide, or a flush
     *                                  immediately followed by location.reload())
     */
    flush(opts = {}) {
        const {keepalive = false, sync = false} = opts;

        if (!this.getOfferId() || !this.queue.length) {
            // No offer to attach to yet (the new-offer route). Hold everything;
            // setOfferId flushes once createOffer returns.
            return;
        }

        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }

        if (sync) {
            this.drain({keepalive: true, terminal: true, token: getCookie('XSRF-TOKEN')});

            return;
        }

        // Not terminal, so it is safe to wait for the token rather than post without
        // one and be rejected by csurf.
        this.primeCsrfToken().then(token => {
            this.drain({keepalive, terminal: keepalive, token});
        });
    }

    /**
     * Posts queued events. A terminal drain empties the queue across as many requests as
     * the server's 20-per-request cap needs, because nothing will run afterwards to send
     * a remainder - the page is unloading or about to reload.
     */
    drain(opts = {}) {
        const {keepalive = false, terminal = false, token = ''} = opts;

        if (!this.queue.length) {
            return;
        }

        /*
         * Posting without the header is not a degraded send, it is a guaranteed 403:
         * /api/refi sits behind csurf, and the request would arrive with no token at all
         * rather than a bad one. Worse, every rejection raises an INVALID_CSRF_TOKEN
         * notification -- an email to every admin -- so a dropped event costs far less
         * than an attempted one.
         *
         * Hold rather than send. A `pagehide` that turns out to be a bfcache suspension
         * can still deliver these when the page is restored, and if the page really is
         * going away they were never going to arrive either way.
         */
        if (!token) {
            if (++this.tokenlessFlushes > MAX_TOKENLESS_FLUSHES) {
                // The cookie is not coming: /api/ping is failing, or this hostname is
                // missing from the server's `csrf.allowedHosts` and so is never issued
                // one. Drop the queue rather than ping every two seconds for the rest
                // of the session.
                this.queue.length = 0;

                return;
            }

            this.scheduleFlush();

            return;
        }

        this.tokenlessFlushes = 0;

        const offerId = this.getOfferId();

        do {
            this.post(this.queue.splice(0, MAX_BATCH), {keepalive, offerId, token});
        } while (terminal && this.queue.length);

        if (this.queue.length) {
            this.scheduleFlush();
        }
    }

    post(events, opts = {}) {
        const {keepalive, offerId, token} = opts;

        try {
            fetch(ENDPOINT, {
                body: JSON.stringify({events, offerId}),
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    ...token && {'X-XSRF-TOKEN': token}
                },
                keepalive,
                method: 'POST'
            }).catch(() => {
                // Telemetry is best effort; a failed post must not surface to the member.
            });
        } catch (ex) {
            // Ditto - and never re-queue, or a persistent failure loops forever.
        }
    }

    primeCsrfToken() {
        try {
            return Promise.resolve(getCsrfToken()).catch(() => getCookie('XSRF-TOKEN'));
        } catch (ex) {
            return Promise.resolve(getCookie('XSRF-TOKEN'));
        }
    }

    // -- Convenience wrappers, so call sites do not repeat the event names -------------

    heroViewed() {
        this.track(RefiFunnelEvents.HERO_VIEWED, {step: 'hero'});
    }

    heroAdvanced() {
        // The CTA navigates immediately, so this one cannot wait for the debounce.
        this.track(RefiFunnelEvents.HERO_ADVANCED, {immediate: true, step: 'hero'});
    }

    stepViewed(step, from) {
        this.track(RefiFunnelEvents.STEP_VIEWED, {from, step});
    }

    authMethodShown(methods) {
        this.track(RefiFunnelEvents.AUTH_METHOD_SHOWN, {detail: {methods}});
    }

    authMethodSelected(method) {
        this.track(RefiFunnelEvents.AUTH_METHOD_SELECTED, {detail: {method}});
    }

    /**
     * How far the member got in a field - never what they typed.
     */
    authFieldProgress(field, method, value) {
        const charCount = String(value == null ? '' : value).length;

        this.track(RefiFunnelEvents.AUTH_FIELD_PROGRESS, {
            detail: {charCount, field, filled: charCount > 0, ...method && {method}}
        });
    }

    authAttempted(method, attemptNumber) {
        this.track(RefiFunnelEvents.AUTH_ATTEMPTED, {
            detail: {method, ...attemptNumber && {attemptNumber}}
        });
    }

    authResult(method, success, reason) {
        this.track(RefiFunnelEvents.AUTH_RESULT, {
            detail: {method, success, ...reason && {reason: String(reason).slice(0, 64)}}
        });
    }

    /**
     * A discrete answer we want a distribution for - a yes/no, never free text.
     */
    fieldAnswered(field, answer) {
        this.track(RefiFunnelEvents.FIELD_ANSWERED, {
            detail: {answer: String(answer).slice(0, 24), field}
        });
    }
}

export default new FunnelService();
